import { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react'
import { EdittedAuthorMap, KojinResult } from 'interface/kojin'
import { EdittedAnnotateMap, KojinAnnotateResult } from 'interface/kojin/annotate'
import { FetchStreamState } from 'shared/api/apiFetchStream'

type EdittableKojinListValue = {
  loading: boolean
  error: Error | null
  progress?: number
  kojinList: KojinResult[] | null
  setAnnotate: (kojinCode: string, annotate: KojinAnnotateResult) => void
  setCandidate: (authorId: number, kojin: KojinResult) => void
  clear: () => void
}
const edittableKojinListContext = createContext<EdittableKojinListValue | null>(null)
export const useEdittableKojinList = () => useContext(edittableKojinListContext)

type EdittableKojinListProviderProps = {
  kojinListState: FetchStreamState<KojinResult>
  children: ReactNode
}
export const EdittableKojinListProvider = ({
	kojinListState,
  children
}: EdittableKojinListProviderProps) => {
  const [edittedAnnotateMap, setEdittedAnnotateMap] = useState<EdittedAnnotateMap | null>(null)
  const [edittedAuthorMap, setEdittedAuthorMap] = useState<EdittedAuthorMap | null>(null)

  const clear = useCallback(
    () => {
      setEdittedAnnotateMap(null)
      setEdittedAuthorMap(null)
    },
    [setEdittedAnnotateMap, setEdittedAuthorMap]
  )
  const setAnnotate = useCallback(
    (kojinCode: string, annotate: KojinAnnotateResult) => {
      const map = edittedAnnotateMap ? { ...edittedAnnotateMap } : {}
      map[kojinCode] = annotate
      setEdittedAnnotateMap(map)
    },
    [edittedAnnotateMap, setEdittedAnnotateMap]
  )
  const setCandidate = useCallback(
    (authorId: number, kojin: KojinResult) => {
      const map = edittedAuthorMap ? { ...edittedAuthorMap } : {}
      map[authorId] = kojin
      setEdittedAuthorMap(map)
    },
    [edittedAuthorMap, setEdittedAuthorMap]
  )

  const edittedKojinList = useMemo(
    () => {
      if (kojinListState.loading || !kojinListState.data) return []
      let kojinList = kojinListState.data
      if (edittedAnnotateMap) {
        kojinList = kojinList.map(kojin => {
          if (kojin.kojinCode) {
            const annotate = edittedAnnotateMap[kojin.kojinCode]
            if (annotate) return { ...kojin, annotate: annotate }
          }
          return kojin
        })
      }
      if (edittedAuthorMap) {
        kojinList = kojinList.map(kojin => {
          if (kojin.authorId) {
            const editted = edittedAuthorMap[kojin.authorId]
            if (editted) return editted
          }
          return kojin
        })
      }
      return kojinList
    },
    [kojinListState, edittedAnnotateMap, edittedAuthorMap]
  )

  const value = useMemo(
    () => ({
      loading: kojinListState.loading,
      error: kojinListState.error,
      progress: kojinListState.loaded && kojinListState.total ? (kojinListState.loaded / kojinListState.total) * 100 : undefined,
      kojinList: edittedKojinList,
      setAnnotate,
      setCandidate,
      clear
    }),
    [kojinListState, edittedKojinList, setAnnotate, setCandidate, clear]
  )
  return (
    <edittableKojinListContext.Provider value={value}>
      {children}
    </edittableKojinListContext.Provider>
  )
}
